import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "../App";
import { toast } from "sonner";
import { getTranslation } from "../i18n/translations";

const ResetPassword = ({ lang = 'tr' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const t = (key) => getTranslation(lang, key);
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('passwordMismatch'));
      return;
    }
    
    if (password.length < 6) {
      toast.error(t('passwordMinLength'));
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token: token,
        new_password: password
      });
      toast.success(t('passwordResetSuccess'));
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || t('passwordResetFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl text-red-600">{t('invalidLink')}</CardTitle>
            <CardDescription>
              {t('invalidLinkDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/")} className="w-full" data-testid="reset-back-home-btn">
              {t('backToHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      {/* Background elements */}
      <div className="absolute top-20 left-0 w-96 h-96 bg-amber-500/20 rounded-full filter blur-3xl"></div>
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-teal-500/20 rounded-full filter blur-3xl"></div>
      
      <Card className="w-full max-w-md relative z-10">
        <CardHeader className="text-center">
          <img 
            src="/nkt-bee-logo.png" 
            alt="NKT Logo" 
            className="mx-auto w-32 h-32 object-contain mb-4"
          />
          <CardTitle className="text-2xl">{t('newPasswordTitle')}</CardTitle>
          <CardDescription>{t('newPasswordDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="new-password">{t('newPassword')}</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">{t('passwordConfirm')}</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
              />
              <p className="text-xs text-slate-500 mt-1">{t('minChars')}</p>
            </div>
            <Button 
              type="submit" 
              disabled={loading} 
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="reset-password-submit-btn"
            >
              {loading ? t('saving') : t('changePasswordBtn')}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate("/")}
              className="w-full"
              data-testid="reset-password-cancel-btn"
            >
              {t('cancel')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
